// What the connect page (`/connect`) tells people to paste into their AI
// assistant. Pure strings, so the page shows and copies exactly these values
// and the tests can check them byte for byte.

/**
 * The production MCP server. The page shows it when runtime config names no
 * usable gateway, so a free account always sees an address that exists.
 */
export const DEFAULT_MCP_SERVER_URL = 'https://mcp.barktechnologies.tech/mcp';

/**
 * The server URL to add in an assistant: `<gateway>/mcp`.
 *
 * The gateway comes from config.json (`mcpGatewayUrl`, read by apiService), so
 * dev shows the dev gateway and prod shows mcp.barktechnologies.tech. A value
 * that is empty or is not an http(s) URL counts as missing.
 */
export function mcpServerUrl(gateway: string | null | undefined): string {
    const base = (gateway ?? '').trim().replace(/\/+$/, '');
    if (!base) return DEFAULT_MCP_SERVER_URL;
    try {
        const { protocol } = new URL(base);
        if (protocol !== 'https:' && protocol !== 'http:') return DEFAULT_MCP_SERVER_URL;
    } catch {
        return DEFAULT_MCP_SERVER_URL;
    }
    return `${base}/mcp`;
}

// Claude opens its "Add custom connector" dialog with these two fields filled
// in. The personal settings page and the workspace admin page take the same
// parameters.
function claudeConnectorLink(page: string, serverUrl: string): string {
    return `${page}?modal=add-custom-connector&connectorName=BARK&connectorUrl=${encodeURIComponent(serverUrl)}`;
}

/** "Add to Claude": opens the person's own connector settings. */
export function claudeAddConnectorUrl(serverUrl: string): string {
    return claudeConnectorLink('https://claude.ai/customize/connectors', serverUrl);
}

/** For a Team or Enterprise owner, who adds BARK once for the workspace. */
export function claudeAdminAddConnectorUrl(serverUrl: string): string {
    return claudeConnectorLink('https://claude.ai/admin-settings/connectors', serverUrl);
}

export const CLAUDE_CONNECTORS_URL = 'https://claude.ai/customize/connectors';
export const CLAUDE_ORGANISATION_CONNECTORS_URL = 'https://claude.ai/admin-settings/connectors';
export const CLAUDE_SETUP_HELP_URL = 'https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp';

/** Forwardable instructions; the MCP URL is distinct from Claude's settings URL. */
export function claudeOwnerInstructions(serverUrl: string): string {
    return [
        'Please add BARK to our Claude organisation.',
        '',
        'This needs an Owner or Primary Owner in Claude. Enterprise also allows a custom role with Manage access to Libraries.',
        '',
        `1. Open Organization settings > Connectors: ${CLAUDE_ORGANISATION_CONNECTORS_URL}`,
        '2. Select Add > Custom > Web.',
        '3. Name: BARK',
        `4. Remote MCP server URL: ${serverUrl}`,
        '5. Select Continue, review the authentication settings and follow the prompts. Select Add to finish.',
        '6. Each member opens Customize > Connectors > BARK > Connect and signs in with their own BARK account.',
        '',
        `Full Claude guide: ${CLAUDE_SETUP_HELP_URL}`,
    ].join('\n');
}

export function codexAddCommand(serverUrl: string): string {
    return `codex mcp add bark --url ${serverUrl}`;
}

/** Sign-in usually starts on its own after `codex mcp add`; this is the fallback. */
export const CODEX_LOGIN_COMMAND = 'codex mcp login bark';

export const CHATGPT_PLUGINS_URL = 'https://chatgpt.com/plugins';
export const OPENAI_ADMIN_URL = 'https://admin.openai.com';
export const OPENAI_PLUGIN_SETUP_HELP = 'https://developers.openai.com/plugins/deploy/connect-chatgpt';
export const OPENAI_PLUGIN_ADMIN_HELP = 'https://learn.chatgpt.com/docs/enterprise/apps-and-connectors';
export const OPENAI_MCP_HELP = 'https://learn.chatgpt.com/docs/extend/mcp';
export const OPENAI_MANAGED_MCP_HELP = 'https://learn.chatgpt.com/docs/enterprise/managed-configuration';

export function chatGptAdminInstructions(serverUrl: string): string {
    return [
        'Please make BARK available in our ChatGPT workspace.',
        `MCP server URL: ${serverUrl}`,
        '',
        'Check the workspace permissions for creating plugins with MCPs, sharing plugins and publishing plugins to the workspace.',
        `If BARK has not been added, open ${CHATGPT_PLUGINS_URL}, select + > Add custom MCP server, and enter the name BARK, a description and the MCP server URL. Configure authentication, review the prompts and select Create as a plugin. Review the discovered tools.`,
        'Publish or share the plugin with the intended members. In the admin console, check plugin installation settings, member permissions and access to the underlying app.',
        'Members then open the approved BARK plugin, install it if needed and connect their own BARK account.',
        `Admin guide: ${OPENAI_PLUGIN_ADMIN_HELP}`,
        `Setup guide: ${OPENAI_PLUGIN_SETUP_HELP}`,
    ].join('\n');
}

export function codexAdminInstructions(serverUrl: string): string {
    return [
        'Please approve BARK for our company-managed Codex clients.',
        'MCP server name: bark',
        `MCP server URL: ${serverUrl}`,
        '',
        'If an MCP server approved list is enforced, both this name and this URL must match an approved entry. Keep existing approved entries.',
        'Approval does not install the server or sign users in. Distribute its configuration, or let members add it locally after approval.',
        `Local setup: ${codexAddCommand(serverUrl)}`,
        `Each member signs in with: ${CODEX_LOGIN_COMMAND}`,
        `Admin guide: ${OPENAI_MANAGED_MCP_HELP}`,
    ].join('\n');
}

/** An entry for an existing requirements policy, not a replacement policy. */
export function codexMcpRequirement(serverUrl: string): string {
    return `[mcp_servers.bark]\nidentity = { url = ${JSON.stringify(serverUrl)} }`;
}

export function claudeCodeAddCommand(serverUrl: string): string {
    return `claude mcp add --scope user --transport http bark ${serverUrl}`;
}

/** Two questions that show what BARK adds, in the order the page lists them. */
export const FIRST_QUESTIONS: readonly string[] = [
    'What operational requirements must liquid assets meet to count in the LCR?',
    'How does the SME supporting factor work, and when does a loan qualify?',
];
